"use client";

import type { ReactNode } from "react";
import { Checkbox } from "../../Checkbox";
import { StackNav, type StackNavItem } from "../../StackNav";
import type { LayerSpec, LayerTreeNode, LayerVisibilityPatch } from "../layers";
import {
  collectLeafLayerIds,
  findDuplicateIds,
  findNode,
  findParent,
  isSingleSelect,
  resolveCheckState,
  resolveSelect,
  resolveToggle,
} from "./layerTree";
import "./LayersPanel.css";

export interface LayersPanelProps {
  height?: number;
  stack?: string[];
  onStackChange?: (stack: string[]) => void;
  tree: LayerTreeNode[];
  /** Source of truth for visibility and legend swatches. */
  layers: LayerSpec[];
  /**
   * Apply a visibility change, given as the target state per affected layer.
   * A container toggle and a single-select choice both arrive as one patch, so
   * the map never renders an intermediate state.
   */
  onVisibilityChange?: (patch: LayerVisibilityPatch) => void;
}

function toStackNavItems(nodes: LayerTreeNode[]): StackNavItem[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.label,
    ...(node.children?.length ? { children: toStackNavItems(node.children) } : {}),
  }));
}

function Swatch({ type, color, icon }: { type: "line" | "dot" | "polygon" | "pin"; color?: string; icon?: ReactNode }) {
  if (icon) {
    return (
      <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center text-text-primary">
        {icon}
      </span>
    );
  }
  const style = color ? { backgroundColor: color } : undefined;

  switch (type) {
    case "line":
      return <span className="inline-block h-0.5 w-4 flex-shrink-0 rounded-full" style={style} />;
    case "polygon":
      return <span className="inline-block h-2.5 w-3.5 flex-shrink-0 rounded-sm border border-border" style={style} />;
    case "pin":
      return (
        <span
          className="inline-block h-2.5 w-2.5 flex-shrink-0 rotate-45 rounded-full rounded-bl-none border border-border"
          style={style}
        />
      );
    default:
      // "dot" and unknown types: no color-ball. With chip rings themed
      // neutral (and status shading owning color going forward), a colored
      // dot in the panel would assert a mapping the map no longer draws.
      return null;
  }
}

/**
 * Radio affordance for one alternative inside a single-select node.
 *
 * Not the `Radio` from RadioGroup: that takes its selected state from a
 * `RadioGroup` ancestor's `value`, and StackNav renders each row through its own
 * `renderRow` call, so there is no shared ancestor to hold the group. This keeps
 * `role="radio"` and `aria-checked` so assistive tech still reads it correctly;
 * what it gives up is arrow-key movement between siblings, which the rows never
 * had — they're an ordinary focusable list.
 */
function RadioDot({ isSelected, onPress, label }: { isSelected: boolean; onPress: () => void; label: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      aria-label={label}
      onClick={onPress}
      className={`grid h-4 w-4 flex-shrink-0 cursor-pointer place-items-center rounded-full border-2 bg-transparent p-0 ${
        isSelected ? "border-action-primary" : "border-border-default"
      }`}
    >
      {isSelected ? <span className="h-1.5 w-1.5 rounded-full bg-action-primary" /> : null}
    </button>
  );
}

function ChevronRight() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0 text-text-secondary"
    >
      <path d="M4.5 2.5 8 6l-3.5 3.5" />
    </svg>
  );
}

const PANEL_HEIGHT = 280;

/**
 * Keeps visibility controls and drill-in navigation as separate click targets.
 * Custom content owns its controls; content with children retains a separate
 * navigation button so those children remain reachable. Visibility is derived
 * from the consumer's layers rather than stored in the navigation stack.
 */
export function LayersPanel({
  tree,
  layers,
  onVisibilityChange,
  stack,
  onStackChange,
  height = PANEL_HEIGHT,
}: LayersPanelProps) {
  const byId = new Map(layers.map((layer) => [layer.id, layer]));
  // A layer the tree names but `layers` doesn't contain is not visible — it isn't
  // on the map at all. Reading the absent case as visible would show a container
  // as fully checked while nothing is drawn, and its toggle would emit ids the
  // consumer never owned. A layer that exists with no `visible` set is visible,
  // matching the rest of the layer system.
  const isVisible = (layerId: string) => {
    const layer = byId.get(layerId);
    return !!layer && layer.visible !== false;
  };

  if (process.env.NODE_ENV !== "production") {
    const duplicates = findDuplicateIds(tree);
    if (duplicates.length > 0) {
      console.warn(
        `[LayersPanel] Duplicate id(s) in \`layerTree\`: ${duplicates.join(", ")}. ` +
          "Row lookups and pane keys resolve to the first match, so the later " +
          "entry is unreachable. Node ids must be unique among node ids, and " +
          "layer ids unique among layer ids."
      );
    }

    const missing = tree.flatMap(collectLeafLayerIds).filter((layerId) => !byId.has(layerId));
    if (missing.length > 0) {
      console.warn(
        `[LayersPanel] \`layerTree\` references layer id(s) not present in \`layers\`: ${missing.join(", ")}. ` +
          "Those rows render as hidden and cannot be turned on."
      );
    }
  }

  const items = toStackNavItems(tree);

  return (
    <div
      className="overflow-hidden"
      style={{ height }}
      // The rail draws a right border for sidebar use, which reads as a stray
      // rule inside a popover.
      data-layers-panel=""
    >
      <StackNav
        stack={stack}
        onStackChange={onStackChange}
        items={items}
        ariaLabel="Map layers"
        animated={false}
        // Rail defaults that fight a popover: a 260px inline width that beats
        // any utility class, a sticky position, and a right border plus opaque
        // surface for sitting beside content. The border and background come
        // from CSS custom properties, so `theme` is the only override that wins
        // against the component's own stylesheet.
        width={null}
        sticky={false}
        theme={{ background: "transparent", borderColor: "transparent" }}
        className="h-full w-full"
        renderRow={({ item, hasChildren, select }) => {
          const node = findNode(tree, item.id);
          if (!node) return null;
          if (node.content !== undefined) {
            return hasChildren ? (
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">{node.content}</div>
                <button
                  type="button"
                  onClick={select}
                  className="flex shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-2 text-text-primary hover:bg-background-hover focus-visible:outline-2 focus-visible:outline-action-default"
                  aria-label={`Open ${node.label}`}
                >
                  <ChevronRight />
                </button>
              </div>
            ) : (
              node.content
            );
          }

          const state = resolveCheckState(node, isVisible);
          const leafLayer = node.layerId ? byId.get(node.layerId) : undefined;
          const legend = leafLayer?.legend;

          const leaves = collectLeafLayerIds(node);
          const visibleCount = leaves.filter(isVisible).length;
          // Only worth saying when it isn't already obvious from the checkbox.
          const subtitle = hasChildren && state === "indeterminate" ? `${visibleCount} of ${leaves.length} on` : null;

          // A row inside a single-select parent is one alternative among
          // siblings, so it gets a radio and choosing it clears the others.
          const parent = findParent(tree, node.id);
          const isAlternative = !!parent && isSingleSelect(parent);

          const apply = (patch: Record<string, boolean>) => {
            if (Object.keys(patch).length > 0) onVisibilityChange?.(patch);
          };

          const control = isAlternative ? (
            <RadioDot
              isSelected={state !== "unchecked"}
              onPress={() => apply(resolveSelect(parent, node.id, isVisible))}
              label={node.label}
            />
          ) : (
            <Checkbox
              isSelected={state === "checked"}
              isIndeterminate={state === "indeterminate"}
              onChange={() => apply(resolveToggle(node, isVisible))}
              aria-label={node.label}
            />
          );

          const label = (
            <span className="flex min-w-0 flex-1 flex-col text-left">
              <span
                className={`truncate text-sm ${state === "unchecked" ? "text-text-secondary" : "text-text-primary"}`}
              >
                {node.label}
              </span>
              {subtitle ? <span className="text-[10px] text-text-secondary">{subtitle}</span> : null}
            </span>
          );

          return (
            // Deliberately not StackNav's row class: that carries `px-3` for a
            // rail, which pushes rows out of line with the popover's own header.
            <div className="edges-layers-panel__row flex cursor-default items-center gap-2 rounded-md py-2.5 transition-colors hover:bg-background-hover focus-within:bg-background-hover">
              {control}
              {/* Everything but the control drills in, chevron included — that
                  arrow is the affordance people aim at. */}
              {hasChildren ? (
                <button
                  type="button"
                  onClick={select}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left"
                  aria-label={`Open ${node.label}`}
                >
                  {legend ? <Swatch type={legend.swatch} color={legend.color} icon={legend.icon} /> : null}
                  {label}
                  <ChevronRight />
                </button>
              ) : (
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  {legend ? <Swatch type={legend.swatch} color={legend.color} icon={legend.icon} /> : null}
                  {label}
                </span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
